'use client';

import { Dispatch, SetStateAction, useEffect, memo } from 'react';
import clsx from 'clsx';
import { ADDED_TO_BASKET_MODAL_ID, DataTestConfig, ThirdPartSelectors } from '@headless-workspace/config';
import { useModalState } from '@headless-workspace/core-ui';
import {
    GlowButton,
    GlowSkeleton,
    GlowTitle,
    Icons,
    PropsWithFontFamily,
    PropsWithLocale,
} from '@headless-workspace/glow-ds';
import { ComponentMemo, ProductType } from '@headless-workspace/typings';
import {
    ActionStatus,
    useProductBasketCTABusinessRules,
    useProductModalDisplay,
    WishlistState,
} from '../../../../../client';
import { VipBrandsValue } from '../../../../../server';

export const SelectorAddToBasketButtonContext = {
    Page: 'page',
    Modal: 'modal',
} as const;

type SelectorAddToBasketButtonContext =
    (typeof SelectorAddToBasketButtonContext)[keyof typeof SelectorAddToBasketButtonContext];

type SelectorAddToBasketProps = PropsWithLocale &
    PropsWithFontFamily & {
        productType: ProductType;
        hasShortLabel: boolean;
        vipBrands: VipBrandsValue;
        isVisible?: boolean;
        setShouldDisplayWishlistButton?: Dispatch<SetStateAction<WishlistState>>;
        productsToAdd: string[];
        productsSetAvailability?: boolean;
        isPriceLegalMentionEnabled?: boolean;
        width?: 'full' | 'auto';
        context: SelectorAddToBasketButtonContext;
    };

export const SelectorAddToBasketButton: ComponentMemo<SelectorAddToBasketProps> = memo(
    ({
        productType,
        hasShortLabel,
        vipBrands,
        locale,
        fontFamily,
        isVisible = false,
        setShouldDisplayWishlistButton,
        productsToAdd,
        productsSetAvailability = false,
        width = 'auto',
        context,
    }) => {
        const productBasketCTAInfo = useProductBasketCTABusinessRules({
            productType,
            productsToAdd,
            productsSetAvailability,
            hasShortLabel,
        });
        const { displayModal } = useProductModalDisplay();
        const { isMenuModalOpen } = useModalState();
        useEffect(() => {
            if (productBasketCTAInfo.isLoading) {
                return setShouldDisplayWishlistButton?.('loading');
            }
            if (productBasketCTAInfo.cta.variant) {
                return setShouldDisplayWishlistButton?.('displayed');
            }
            return setShouldDisplayWishlistButton?.('hidden');
        });

        const ctaClickHandler = async () => {
            if (productBasketCTAInfo.isLoading) {
                return;
            }

            if (productBasketCTAInfo.cta.actionStatus === ActionStatus.AddToBasketCondition) {
                const productAddedToBasket = await productBasketCTAInfo.cta.onClick();

                if (!productAddedToBasket) {
                    return;
                }

                if (!isMenuModalOpen(ADDED_TO_BASKET_MODAL_ID)) {
                    return displayModal({
                        actionStatus: productBasketCTAInfo.cta.actionStatus,
                        productAddedToBasket,
                        vipBrands,
                        locale,
                    });
                }
            }

            if (productBasketCTAInfo.cta.actionStatus === ActionStatus.NotifyMeCondition) {
                return displayModal({
                    actionStatus: productBasketCTAInfo.cta.actionStatus,
                    productIds: productsToAdd,
                    locale,
                });
            }

            if (
                productBasketCTAInfo.cta.actionStatus === ActionStatus.ChooseStoreCondition ||
                productBasketCTAInfo.cta.actionStatus === ActionStatus.ChangeStoreCondition
            ) {
                return displayModal({
                    actionStatus: productBasketCTAInfo.cta.actionStatus,
                    storeModalInfo: productBasketCTAInfo.cta.storeModalInfo,
                    locale,
                    hasBackCta: context === SelectorAddToBasketButtonContext.Modal,
                });
            }
        };

        if (productBasketCTAInfo.isLoading) {
            return (
                <GlowSkeleton
                    containerClassName={clsx(
                        'h-[3rem] flex-1',
                        isVisible ? 'w-full' : 'w-[10.469rem] tablet:w-[12.875rem]',
                    )}
                />
            );
        }

        if (productBasketCTAInfo.cta.variant) {
            const buttonIcon =
                productBasketCTAInfo.cta.actionStatus === ActionStatus.NotifyMeCondition
                    ? Icons[productBasketCTAInfo.cta.iconName]
                    : undefined;

            return (
                <GlowButton
                    dataTestId={DataTestConfig.testIds.addToBasketButton}
                    className={clsx(
                        'transition-none overflow-hidden flex-1',
                        !isVisible && 'desktopS:flex-none',
                        context === SelectorAddToBasketButtonContext.Page && ThirdPartSelectors.PurchaseInAppCtaClass,
                    )}
                    Icon={buttonIcon}
                    label={productBasketCTAInfo.cta.label}
                    onClick={ctaClickHandler}
                    isSpinnerActive={productBasketCTAInfo.isAddingToBasket}
                    variant={productBasketCTAInfo.cta.variant}
                    size={'large'}
                    fontFamily={fontFamily}
                    width={width}
                />
            );
        }

        return (
            <GlowTitle
                text={productBasketCTAInfo.cta.label}
                fontSize={'title4'}
                className={'text-text-warning py-0.75 flex-1'}
            />
        );
    },
);

SelectorAddToBasketButton.displayName = 'SelectorAddToBasketButton';
